import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import HoldingsTable from "../../../components/allocations/HoldingsTable";
import AssetAllocationChart from "../../../components/allocations/AssetAllocationChart";
import SectorAllocationChart from "../../../components/allocations/SectorAllocationChart";
import PerformanceChart from "../../../components/allocations/PerformanceChart";
import useClientDatabase from "../../../hooks/useClientDatabase";
import useAccountHoldings from "../../../hooks/useAccountHoldings";
import SymbolDetailModal from "../../../components/allocations/SymbolDetailModal";
import { useTheme } from '../../../contexts/ThemeContext';
import { getHoldings } from "../../../utils/apiConfig";

import logger from '../../../utils/logger';

// Utility: Convert allocation map to Chart.js data format
function allocationToChartData(allocationMap, palette) {
  const labels = Object.keys(allocationMap || {});
  const data = Object.values(allocationMap || {});
  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor: palette || [
          "#8B0000",
          "#4682B4",
          "#CD853F",
          "#6495ED",
          "#800080",
          "#90EE90",
          "#FFD700",
          "#FF69B4",
          "#A0522D",
          "#2E8B57",
        ],
      },
    ],
  };
}

function getAccountDisplayName(h) {
  if (!h) return "";
  const first = h.first_name || "";
  const last = h.last_name || "";
  const secFirst = h.secondary_first_name || "";
  const secLast = h.secondary_last_name || "";
  const accountType = h.account_type_name || "";
  const taxQual = h.tax_qualification_name || "";
  let name = `${first} ${last}`.trim();
  if (secFirst || secLast) {
    const secName = `${secFirst} ${secLast}`.trim();
    if (secName) name += ` & ${secName}`;
  }
  if (accountType) {
    if (taxQual && !accountType.toLowerCase().includes(taxQual.toLowerCase())) {
      name += ` ${accountType} (${taxQual})`;
    } else {
      name += ` ${accountType}`;
    }
  } else if (taxQual) {
    name += ` (${taxQual})`;
  }
  return name.trim();
}

const AccountView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const currentPath = location.pathname;
  const { accounts, loading, error } = useClientDatabase();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [expanded, setExpanded] = useState(null);
  const [sortField, setSortField] = useState("accountNumber");
  const [sortDirection, setSortDirection] = useState("asc");
  const [accountHoldings, setAccountHoldings] = useState({
    loading: false,
    error: null,
    holdings: []
  });
  const { theme } = useTheme();
  logger.info("[AccountView] accounts:", accounts);
  if (accounts && accounts.length > 0) {
    logger.info("[AccountView] first account:", accounts[0]);
  }
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // If routed as /account-allocations/:accountNumber, show detail view
  const accountNumber = params.accountNumber;
  const accountDetail = accounts.find(
    (acc) => acc.accountNumber === accountNumber,
  );
  const {
    holdings,
    assetAllocation,
    sectorAllocation,
    totalMarketValue,
    performanceData,
    loading: accountHoldingsLoading,
    error: accountHoldingsError,
  } = useAccountHoldings(accountNumber);

  // Load account holdings when expanded
  useEffect(() => {
    if (!expanded) {
      setAccountHoldings({
        loading: false,
        error: null,
        holdings: []
      });
      return;
    }

    const fetchHoldings = async () => {
      setAccountHoldings(prev => ({ ...prev, loading: true, error: null }));
      try {
        const res = await getHoldings({ account_number: expanded });
        setAccountHoldings({
          loading: false,
          error: null,
          holdings: res.data || []
        });
      } catch (err) {
        setAccountHoldings({
          loading: false,
          error: err.message || "Failed to load holdings",
          holdings: []
        });
      }
    };

    fetchHoldings();
  }, [expanded]);

  // Sort accounts
  const sortedAccounts = useMemo(() => {
    return [...accounts].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [accounts, sortField, sortDirection]);

  // Filter accounts
  const filteredAccounts = useMemo(() => {
    return sortedAccounts.filter(
      (acc) =>
        !search ||
        acc.accountNumber.toLowerCase().includes(search.toLowerCase()) ||
        (acc.accountName || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [sortedAccounts, search]);

  // Page accounts
  const pagedAccounts = useMemo(() => {
    return filteredAccounts.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage
    );
  }, [filteredAccounts, page, itemsPerPage]);

  // Table columns
  const columns = [
    { Header: "Account #", accessor: "accountNumber" },
    { Header: "Household", accessor: "householdName" },
    {
      Header: "Total Market Value",
      accessor: "totalMarketValue",
      Cell: (row) =>
        row.totalMarketValue
          ? `$${row.totalMarketValue.toLocaleString()}`
          : "-",
    },
    {
      Header: "# Securities",
      accessor: "securityCount",
      Cell: (row) => row.securityCount || "-",
    },
  ];

  const navButtons = [
    { label: "Firm Allocations", route: "/portfolio-management/firm-allocations" },
    { label: "Advisor View", route: "/portfolio-management/advisor-allocations" },
    { label: "Household View", route: "/portfolio-management/household-allocations" },
    { label: "Account View", route: "/portfolio-management/account-allocations" },
  ];

  return (
    <SubPageLayout
      title="Account Allocations"
      subtitle="View and analyze account-level portfolio allocations"
      breadcrumbItems={[
        { label: "Portfolio Management", link: "/portfolio-management" },
        {
          label: "Account Allocations",
          link: "/portfolio-management/account-allocations",
        },
      ]}
    >
      {/* Navigation Buttons */}
      <div className="flex space-x-4 mb-6">
        {navButtons.map((btn) => (
          <button
            key={btn.route}
            className={`px-4 py-2 rounded-lg font-medium transition-colors duration-150 ${
              currentPath === btn.route 
                ? "bg-blue-600 text-white dark:bg-blue-500" 
                : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
            onClick={() => navigate(btn.route)}
            disabled={currentPath === btn.route}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="mb-4 flex justify-between items-center">
        <div className="flex-1 max-w-sm">
          <input
            type="text"
            placeholder="Search accounts..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          />
        </div>
        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
          <span>
            {filteredAccounts.length} account{filteredAccounts.length !== 1 ? "s" : ""}
          </span>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Previous
            </button>
            <span className="px-2">
              Page {page} of {Math.ceil(filteredAccounts.length / itemsPerPage)}
            </span>
            <button
              onClick={() =>
                setPage(
                  Math.min(
                    Math.ceil(filteredAccounts.length / itemsPerPage),
                    page + 1,
                  ),
                )
              }
              disabled={
                page >= Math.ceil(filteredAccounts.length / itemsPerPage)
              }
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="w-8 px-2 py-2"></th>
              {columns.map((col) => (
                <th
                  key={col.accessor}
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200"
                  style={{ cursor: col.sortable ? "pointer" : "default" }}
                  onClick={() => {
                    if (!col.sortable) return;
                    if (sortField === col.sortKey) {
                      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
                    } else {
                      setSortField(col.sortKey);
                      setSortDirection("asc");
                    }
                  }}
                >
                  <div className="flex items-center space-x-1">
                    <span>{col.Header}</span>
                    {col.sortable && (
                      <span className="text-gray-400 dark:text-gray-500">
                        {sortField === col.sortKey ? (
                          sortDirection === "asc" ? (
                            <FaSortUp />
                          ) : (
                            <FaSortDown />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {pagedAccounts.map((account) => (
              <React.Fragment key={account.accountNumber}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                  <td className="px-2 py-2">
                    <button
                      onClick={() =>
                        setExpanded(
                          expanded === account.accountNumber ? null : account.accountNumber,
                        )
                      }
                      className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {expanded === account.accountNumber ? "▼" : "▶"}
                    </button>
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col.accessor}
                      className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100"
                    >
                      {col.Cell ? col.Cell(account) : account[col.accessor]}
                    </td>
                  ))}
                </tr>
                {expanded === account.accountNumber && accountHoldings && (
                  <tr>
                    <td colSpan={columns.length + 1} className="bg-gray-50 dark:bg-gray-800/50">
                      {/* Error state */}
                      {accountHoldings.error ? (
                        <div className="p-4 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded mb-4">
                          Unable to load holdings for this account: {accountHoldings.error}
                        </div>
                      ) : accountHoldings.loading ? (
                        <div className="p-4 text-gray-500 dark:text-gray-400 italic">
                          Loading holdings…
                        </div>
                      ) : (
                        <div className="p-4">
                          {/* Expanded content */}
                          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
                            {/* Holdings table */}
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead>
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Symbol
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Security Type
                                  </th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Quantity
                                  </th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Market Value
                                  </th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Last Trade Date
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {/* Holdings rows */}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </SubPageLayout>
  );
};

export default AccountView;
